'use client'

import { useBulkUpdateConnectionsMutation } from '@/api/files/bulk-update-connections-mutation'
import { useGetLabelsQuery } from '@/api/files/labels/get-labels-query'
import { INode } from '@/api/nodes/mutations/directory/create-directory-mutation'
import { useGetProductsQuery } from '@/api/products/queries/get-products-query'
import { Button } from '@/components/ui/button'
import { GeneralModal, IconBox } from '@/components/ui/general-modal'
import { Input } from '@/components/ui/input'
import { useDirectorySelectionContext } from '@/features/drive/functionalities/selection/directory-selection-context'
import { cn } from '@/utils/styles/cn'
import {
    getDirectorySelectionScope,
    getConnectionSelectionDisplayState,
} from '@/features/drive/functionalities/selection/directory-selection-scope'
import { Loader2, Minus, Package, Plus, RotateCcw, Tags, type LucideIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

type ISelectedFileNode = INode & { file: NonNullable<INode['file']> }

type IScopeLabelSummary = {
    uuid: string
    value: string
    system: boolean
    matchingFileCount: number
}

type IScopeProductSummary = {
    uuid: string
    value: string
    matchingFileCount: number
}

type IConnectionDisplayState = 'selected' | 'partial' | 'unselected' | 'pending-add' | 'pending-remove'
type IRowActionTone = 'primary' | 'secondary' | 'danger'

interface IDirectorySelectionBulkDialogProps {
    isOpen: boolean
    onOpenChange: (isOpen: boolean) => void
}

interface IConnectionRowAction {
    label: string
    onClick: () => void
    tone: IRowActionTone
    icon: LucideIcon
}

const isFileNode = (node: INode): node is ISelectedFileNode => node.type === 'FILE' && !!node.file

const DISPLAY_STATE_ORDER: Record<IConnectionDisplayState, number> = {
    'pending-add': 0,
    'pending-remove': 1,
    partial: 2,
    selected: 3,
    unselected: 4,
}

const STATUS_BADGE_CLASS_NAME: Record<IConnectionDisplayState, string> = {
    selected: 'bg-[#EDF8F0] text-[#2B6B3F]',
    partial: 'bg-[#FFF4E9] text-[#A45C1C]',
    unselected: 'bg-black/[0.04] text-black/50',
    'pending-add': 'bg-[#FFF0E4] text-[#C75813]',
    'pending-remove': 'bg-[#FFF1F1] text-[#B14646]',
}

const ACTION_CLASS_NAME: Record<IRowActionTone, string> = {
    primary:
        'border-[#F6C29D] bg-[#FFF6EF] text-[#C75813] shadow-[0px_0px_0px_1px_rgba(222,96,10,0.06),0px_1px_2px_rgba(0,0,0,0.06)] hover:bg-[#FFEBDD]',
    secondary:
        'border-black/10 bg-white text-black/65 shadow-[0px_0px_0px_1px_rgba(0,0,0,0.04),0px_1px_2px_rgba(0,0,0,0.05)] hover:bg-black/[0.03]',
    danger: 'border-[#F0CACA] bg-[#FFF6F6] text-[#B14646] shadow-[0px_0px_0px_1px_rgba(177,70,70,0.06),0px_1px_2px_rgba(0,0,0,0.05)] hover:bg-[#FFEDED]',
}

const useSelectedFileScope = () => {
    const {
        state: { selectedNodes },
    } = useDirectorySelectionContext()

    const selectedFileNodes = useMemo(() => selectedNodes?.filter(isFileNode) ?? [], [selectedNodes])
    const selectedFileUuids = useMemo(() => selectedFileNodes.map((node) => node.file.uuid), [selectedFileNodes])
    const selectedNodeUuids = useMemo(
        () =>
            selectedNodes?.filter((node) => isFileNode(node) || node.type === 'DIRECTORY').map((node) => node.uuid) ??
            [],
        [selectedNodes]
    )
    const { folderCount, ignoredProductCount } = getDirectorySelectionScope(selectedNodes ?? [])
    const ignoredSelectionCount = Math.max(0, (selectedNodes?.length ?? 0) - selectedFileNodes.length)

    return {
        ignoredSelectionCount,
        selectedFileNodes,
        selectedFileUuids,
        selectedNodeUuids,
        folderCount,
        ignoredProductCount,
    }
}

const SelectionStatusPill = ({ label, state }: { label: string; state: IConnectionDisplayState }) => (
    <span
        className={cn(
            'inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium',
            STATUS_BADGE_CLASS_NAME[state]
        )}
    >
        {label}
    </span>
)

const SelectionActionButton = ({
    disabled = false,
    icon: Icon,
    label,
    onClick,
    tone,
}: IConnectionRowAction & { disabled?: boolean }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={cn(
            'inline-flex h-7 items-center gap-1 rounded-md border px-2.5 text-[11px] font-medium transition-colors disabled:cursor-default disabled:opacity-50',
            ACTION_CLASS_NAME[tone]
        )}
    >
        <Icon className="h-3 w-3" />
        {label}
    </button>
)

const SelectionListRow = ({
    actions,
    actionsDisabled = false,
    name,
    statusLabel,
    statusState,
    systemLabel,
}: {
    actions: IConnectionRowAction[]
    actionsDisabled?: boolean
    name: string
    statusLabel: string
    statusState: IConnectionDisplayState
    systemLabel?: string
}) => (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-black/8 bg-white px-4 py-3">
        <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
                <div className="truncate text-sm font-medium text-black/80">{name}</div>

                {systemLabel && (
                    <span className="inline-flex items-center rounded-full bg-[#FFF4EC] px-2 py-0.5 text-[10px] font-medium text-[#8A4B21]">
                        {systemLabel}
                    </span>
                )}
            </div>

            <div className="mt-1">
                <SelectionStatusPill label={statusLabel} state={statusState} />
            </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
            {actions.map((action) => (
                <SelectionActionButton
                    key={action.label}
                    disabled={actionsDisabled}
                    icon={action.icon}
                    label={action.label}
                    onClick={action.onClick}
                    tone={action.tone}
                />
            ))}
        </div>
    </div>
)

export const DirectorySelectionBulkLabelsDialog = ({ isOpen, onOpenChange }: IDirectorySelectionBulkDialogProps) => {
    const [labelSearch, setLabelSearch] = useState('')
    const [selectedAddLabelUuids, setSelectedAddLabelUuids] = useState<string[]>([])
    const [selectedRemoveLabelUuids, setSelectedRemoveLabelUuids] = useState<string[]>([])

    const bulkUpdateConnectionsMutation = useBulkUpdateConnectionsMutation('labels')
    const { data: labels, isLoading: isLoadingLabels } = useGetLabelsQuery({ search: undefined })
    const { ignoredSelectionCount, selectedFileNodes, selectedFileUuids } = useSelectedFileScope()

    const scopeLabels = useMemo(() => {
        const labelMap = new Map<string, IScopeLabelSummary>()

        selectedFileNodes.forEach((node) => {
            node.file.connections?.labels.forEach((label) => {
                const existing = labelMap.get(label.uuid)

                if (existing) {
                    existing.matchingFileCount += 1
                    return
                }

                labelMap.set(label.uuid, {
                    uuid: label.uuid,
                    value: label.value,
                    system: label.system,
                    matchingFileCount: 1,
                })
            })
        })

        return labelMap
    }, [selectedFileNodes])

    const trimAndLowerLabelSearch = labelSearch.trim().toLowerCase()
    const hasPendingChanges = selectedAddLabelUuids.length > 0 || selectedRemoveLabelUuids.length > 0

    useEffect(() => {
        if (isOpen) return

        setLabelSearch('')
        setSelectedAddLabelUuids([])
        setSelectedRemoveLabelUuids([])
    }, [isOpen])

    const queueAddLabel = (labelUuid: string) => {
        setSelectedAddLabelUuids((current) => (current.includes(labelUuid) ? current : [...current, labelUuid]))
        setSelectedRemoveLabelUuids((current) => current.filter((uuid) => uuid !== labelUuid))
    }

    const queueRemoveLabel = (labelUuid: string) => {
        setSelectedRemoveLabelUuids((current) => (current.includes(labelUuid) ? current : [...current, labelUuid]))
        setSelectedAddLabelUuids((current) => current.filter((uuid) => uuid !== labelUuid))
    }

    const clearLabelChange = (labelUuid: string) => {
        setSelectedAddLabelUuids((current) => current.filter((uuid) => uuid !== labelUuid))
        setSelectedRemoveLabelUuids((current) => current.filter((uuid) => uuid !== labelUuid))
    }

    const handleApplyChanges = async () => {
        if (!hasPendingChanges || selectedFileUuids.length === 0) return

        await bulkUpdateConnectionsMutation.mutateAsync({
            fileUuids: selectedFileUuids,
            labelUuidsToAdd: selectedAddLabelUuids,
            labelUuidsToRemove: selectedRemoveLabelUuids,
            productUuidsToAdd: [],
            productUuidsToRemove: [],
        })

        onOpenChange(false)
    }

    const filteredLabels = useMemo(() => {
        if (!labels) return []

        return labels
            .filter((label) => {
                if (!trimAndLowerLabelSearch) return true

                return (
                    label.value.toLowerCase().includes(trimAndLowerLabelSearch) ||
                    label.category?.toLowerCase().includes(trimAndLowerLabelSearch)
                )
            })
            .map((label) => {
                const matchingFileCount = scopeLabels.get(label.uuid)?.matchingFileCount ?? 0
                const displayState = getConnectionSelectionDisplayState({
                    hasPendingAdd: selectedAddLabelUuids.includes(label.uuid),
                    hasPendingRemove: selectedRemoveLabelUuids.includes(label.uuid),
                    matchingFileCount,
                    selectedFileCount: selectedFileNodes.length,
                })

                const statusLabel = {
                    selected: 'On all selected assets',
                    partial: 'On some selected assets',
                    unselected: 'Not on selected assets',
                    'pending-add': 'Adding to all selected assets',
                    'pending-remove': 'Removing from selected assets',
                }[displayState]

                const actions: IConnectionRowAction[] =
                    displayState === 'selected'
                        ? [
                              {
                                  label: 'Remove',
                                  onClick: () => queueRemoveLabel(label.uuid),
                                  tone: 'secondary',
                                  icon: Minus,
                              },
                          ]
                        : displayState === 'unselected'
                          ? [
                                {
                                    label: 'Add',
                                    onClick: () => queueAddLabel(label.uuid),
                                    tone: 'primary',
                                    icon: Plus,
                                },
                            ]
                          : displayState === 'partial'
                            ? [
                                  {
                                      label: 'Remove from selected',
                                      onClick: () => queueRemoveLabel(label.uuid),
                                      tone: 'secondary',
                                      icon: Minus,
                                  },
                                  {
                                      label: 'Add to all selected',
                                      onClick: () => queueAddLabel(label.uuid),
                                      tone: 'primary',
                                      icon: Plus,
                                  },
                              ]
                            : [
                                  {
                                      label: 'Undo',
                                      onClick: () => clearLabelChange(label.uuid),
                                      tone: displayState === 'pending-remove' ? 'danger' : 'secondary',
                                      icon: RotateCcw,
                                  },
                              ]

                return {
                    actions,
                    displayState,
                    matchingFileCount,
                    statusLabel,
                    ...label,
                }
            })
            .sort((left, right) => {
                const leftOrder = DISPLAY_STATE_ORDER[left.displayState]
                const rightOrder = DISPLAY_STATE_ORDER[right.displayState]

                if (leftOrder !== rightOrder) {
                    return leftOrder - rightOrder
                }

                if (left.matchingFileCount !== right.matchingFileCount) {
                    return right.matchingFileCount - left.matchingFileCount
                }

                if (left.system !== right.system) {
                    return left.system ? -1 : 1
                }

                return left.value.localeCompare(right.value)
            })
    }, [
        labels,
        trimAndLowerLabelSearch,
        scopeLabels,
        selectedAddLabelUuids,
        selectedRemoveLabelUuids,
        selectedFileNodes.length,
    ])

    return (
        <GeneralModal
            open={isOpen}
            onOpenChange={onOpenChange}
            title="Edit labels"
            subtitle={
                selectedFileNodes.length === 1 ? '1 selected asset' : `${selectedFileNodes.length} selected assets`
            }
            icon={
                <IconBox>
                    <Tags className="h-5 w-5 text-[#6F6F6F]" />
                </IconBox>
            }
            className="md:w-[640px]"
            bodyClassName="overflow-y-auto"
        >
            <div className="flex flex-col gap-4">
                {ignoredSelectionCount > 0 && (
                    <div className="rounded-lg border border-dashed border-[#FF7A33]/18 bg-[#FFF4EC] px-3 py-3 text-sm text-[#8A4B21]">
                        {ignoredSelectionCount === 1
                            ? '1 selected item is not an asset file and will be ignored.'
                            : `${ignoredSelectionCount} selected items are not asset files and will be ignored.`}
                    </div>
                )}

                <Input
                    value={labelSearch}
                    onChange={(event) => setLabelSearch(event.target.value)}
                    placeholder="Search labels"
                />

                <div className="max-h-[380px] space-y-2 overflow-y-auto rounded-xl border border-black/8 bg-black/[0.02] p-2">
                    {isLoadingLabels ? (
                        <div className="flex items-center gap-2 px-4 py-6 text-sm text-black/50">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading labels
                        </div>
                    ) : filteredLabels.length > 0 ? (
                        filteredLabels.map((label) => (
                            <SelectionListRow
                                key={label.uuid}
                                actions={label.actions}
                                actionsDisabled={bulkUpdateConnectionsMutation.isPending}
                                name={label.value}
                                statusLabel={label.statusLabel}
                                statusState={label.displayState}
                                systemLabel={label.system ? 'System' : undefined}
                            />
                        ))
                    ) : (
                        <div className="px-4 py-6 text-sm text-black/45">No labels found</div>
                    )}
                </div>
            </div>

            <div className="flex justify-end gap-2 mt-4">
                <Button type="button" variant="ds2-secondary" size="ds2" onClick={() => onOpenChange(false)}>
                    Cancel
                </Button>

                <Button
                    type="button"
                    variant="ds2-primary"
                    size="ds2"
                    onClick={handleApplyChanges}
                    disabled={!hasPendingChanges || bulkUpdateConnectionsMutation.isPending}
                >
                    {bulkUpdateConnectionsMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                    Save labels
                </Button>
            </div>
        </GeneralModal>
    )
}

export const DirectorySelectionBulkProductsDialog = ({ isOpen, onOpenChange }: IDirectorySelectionBulkDialogProps) => {
    const [productSearch, setProductSearch] = useState('')
    const [selectedAddProductUuids, setSelectedAddProductUuids] = useState<string[]>([])
    const [selectedRemoveProductUuids, setSelectedRemoveProductUuids] = useState<string[]>([])

    const bulkUpdateConnectionsMutation = useBulkUpdateConnectionsMutation('products')
    const { data: products, isLoading: isLoadingProducts } = useGetProductsQuery()
    const { folderCount, ignoredProductCount, selectedFileNodes, selectedFileUuids, selectedNodeUuids } =
        useSelectedFileScope()

    const scopeProducts = useMemo(() => {
        const productMap = new Map<string, IScopeProductSummary>()

        selectedFileNodes.forEach((node) => {
            node.file.connections?.products.forEach((product) => {
                const existing = productMap.get(product.uuid)

                if (existing) {
                    existing.matchingFileCount += 1
                    return
                }

                productMap.set(product.uuid, {
                    uuid: product.uuid,
                    value: product.name,
                    matchingFileCount: 1,
                })
            })
        })

        return productMap
    }, [selectedFileNodes])

    const hasPendingChanges = selectedAddProductUuids.length > 0 || selectedRemoveProductUuids.length > 0
    const normalizedProductSearch = productSearch.trim().toLowerCase()
    const showSearch = (products?.length ?? 0) > 6

    useEffect(() => {
        if (isOpen) return

        setProductSearch('')
        setSelectedAddProductUuids([])
        setSelectedRemoveProductUuids([])
    }, [isOpen])

    const queueAddProduct = (productUuid: string) => {
        setSelectedAddProductUuids((current) => (current.includes(productUuid) ? current : [...current, productUuid]))
        setSelectedRemoveProductUuids((current) => current.filter((uuid) => uuid !== productUuid))
    }

    const queueRemoveProduct = (productUuid: string) => {
        setSelectedRemoveProductUuids((current) =>
            current.includes(productUuid) ? current : [...current, productUuid]
        )
        setSelectedAddProductUuids((current) => current.filter((uuid) => uuid !== productUuid))
    }

    const clearProductChange = (productUuid: string) => {
        setSelectedAddProductUuids((current) => current.filter((uuid) => uuid !== productUuid))
        setSelectedRemoveProductUuids((current) => current.filter((uuid) => uuid !== productUuid))
    }

    const handleApplyChanges = async () => {
        if (!hasPendingChanges || selectedNodeUuids.length === 0) return

        await bulkUpdateConnectionsMutation.mutateAsync({
            fileUuids: selectedFileUuids,
            nodeUuids: selectedNodeUuids,
            labelUuidsToAdd: [],
            labelUuidsToRemove: [],
            productUuidsToAdd: selectedAddProductUuids,
            productUuidsToRemove: selectedRemoveProductUuids,
        })

        onOpenChange(false)
    }

    const filteredProducts = useMemo(() => {
        if (!products) return []

        return products
            .filter((product) => {
                if (!normalizedProductSearch) return true

                return product.name.toLowerCase().includes(normalizedProductSearch)
            })
            .map((product) => {
                const matchingFileCount = scopeProducts.get(product.uuid)?.matchingFileCount ?? 0
                const displayState = getConnectionSelectionDisplayState({
                    hasPendingAdd: selectedAddProductUuids.includes(product.uuid),
                    hasPendingRemove: selectedRemoveProductUuids.includes(product.uuid),
                    matchingFileCount,
                    selectedFileCount: selectedFileNodes.length,
                    includesFolders: folderCount > 0,
                })

                const statusLabel = {
                    selected: 'Connected to all selected assets',
                    partial:
                        folderCount > 0
                            ? 'Folder contents will be included when saving'
                            : 'Connected to some selected assets',
                    unselected: 'Not connected to selected assets',
                    'pending-add': 'Connecting to all selected assets',
                    'pending-remove': 'Disconnecting from selected assets',
                }[displayState]

                const actions: IConnectionRowAction[] =
                    displayState === 'selected'
                        ? [
                              {
                                  label: 'Disconnect',
                                  onClick: () => queueRemoveProduct(product.uuid),
                                  tone: 'secondary',
                                  icon: Minus,
                              },
                          ]
                        : displayState === 'unselected'
                          ? [
                                {
                                    label: 'Connect',
                                    onClick: () => queueAddProduct(product.uuid),
                                    tone: 'primary',
                                    icon: Plus,
                                },
                            ]
                          : displayState === 'partial'
                            ? [
                                  {
                                      label: 'Disconnect selected',
                                      onClick: () => queueRemoveProduct(product.uuid),
                                      tone: 'secondary',
                                      icon: Minus,
                                  },
                                  {
                                      label: 'Connect all selected',
                                      onClick: () => queueAddProduct(product.uuid),
                                      tone: 'primary',
                                      icon: Plus,
                                  },
                              ]
                            : [
                                  {
                                      label: 'Undo',
                                      onClick: () => clearProductChange(product.uuid),
                                      tone: displayState === 'pending-remove' ? 'danger' : 'secondary',
                                      icon: RotateCcw,
                                  },
                              ]

                return {
                    actions,
                    displayState,
                    matchingFileCount,
                    statusLabel,
                    ...product,
                }
            })
            .sort((left, right) => {
                const leftOrder = DISPLAY_STATE_ORDER[left.displayState]
                const rightOrder = DISPLAY_STATE_ORDER[right.displayState]

                if (leftOrder !== rightOrder) {
                    return leftOrder - rightOrder
                }

                if (left.matchingFileCount !== right.matchingFileCount) {
                    return right.matchingFileCount - left.matchingFileCount
                }

                return left.name.localeCompare(right.name)
            })
    }, [
        products,
        normalizedProductSearch,
        scopeProducts,
        selectedAddProductUuids,
        selectedRemoveProductUuids,
        selectedFileNodes.length,
        folderCount,
    ])

    return (
        <GeneralModal
            open={isOpen}
            onOpenChange={onOpenChange}
            title="Edit products"
            subtitle={selectedNodeUuids.length === 1 ? '1 selected item' : `${selectedNodeUuids.length} selected items`}
            icon={
                <IconBox>
                    <Package className="h-5 w-5 text-[#6F6F6F]" />
                </IconBox>
            }
            className="md:w-[640px]"
            bodyClassName="overflow-y-auto"
        >
            <div className="flex flex-col gap-4">
                {folderCount > 0 && (
                    <div className="rounded-lg border border-dashed border-[#FF7A33]/18 bg-[#FFF4EC] px-3 py-3 text-sm text-[#8A4B21]">
                        {folderCount === 1
                            ? 'Images inside the selected folder will be included.'
                            : `Images inside ${folderCount} selected folders will be included.`}
                    </div>
                )}

                {ignoredProductCount > 0 && (
                    <p className="text-sm text-black/45">
                        {ignoredProductCount} unsupported {ignoredProductCount === 1 ? 'item' : 'items'} will be
                        ignored.
                    </p>
                )}

                {showSearch && (
                    <Input
                        value={productSearch}
                        onChange={(event) => setProductSearch(event.target.value)}
                        placeholder="Find products"
                    />
                )}

                <div className="max-h-[380px] space-y-2 overflow-y-auto rounded-xl border border-black/8 bg-black/[0.02] p-2">
                    {isLoadingProducts ? (
                        <div className="flex items-center gap-2 px-4 py-6 text-sm text-black/50">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading products
                        </div>
                    ) : filteredProducts.length > 0 ? (
                        filteredProducts.map((product) => (
                            <SelectionListRow
                                key={product.uuid}
                                actions={product.actions}
                                actionsDisabled={bulkUpdateConnectionsMutation.isPending}
                                name={product.name}
                                statusLabel={product.statusLabel}
                                statusState={product.displayState}
                            />
                        ))
                    ) : (
                        <div className="px-4 py-6 text-sm text-black/45">No products found</div>
                    )}
                </div>
            </div>

            <div className="flex justify-end gap-2 mt-4">
                <Button type="button" variant="ds2-secondary" size="ds2" onClick={() => onOpenChange(false)}>
                    Cancel
                </Button>

                <Button
                    type="button"
                    variant="ds2-primary"
                    size="ds2"
                    onClick={handleApplyChanges}
                    disabled={!hasPendingChanges || bulkUpdateConnectionsMutation.isPending}
                >
                    {bulkUpdateConnectionsMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                    Save products
                </Button>
            </div>
        </GeneralModal>
    )
}
